import { useLazyLoadQuery } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import { ProfileHeaderQuery } from 'graphql/__generated__/ProfileHeaderQuery.graphql';
import { PROFILE_HEADER_QUERY } from 'graphql/ProfileHeaderQuery';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, { ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import useLoggedInUser from '../../../../utils/useLoggedInUser';
import React, { useCallback, useState } from 'react';
import {
  sendRecognitionModalOpenClickEvent,
  sendRecognitionButtonClickEvent,
} from 'analytics/kudosAnalytics';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import KudosModalBody from './KudosModalBody';

const KudosModal = ({ internalId }: { internalId: string }) => {
  const [pathProfileId] = usePathParam('pathProfileId');
  const profileResponseData = useLazyLoadQuery<ProfileHeaderQuery>(
    PROFILE_HEADER_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );

  const profileData = profileResponseData?.unified?.unifiedProfile;
  if (!profileData) {
    return <div>{'Could not fetch profile data'}</div>;
  }

  const userResponseData = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
    ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
    { accountId: profileData.aaid as string },
    { fetchPolicy: 'store-or-network' },
  );

  const userData = userResponseData.user;
  if (!userData) {
    return <div>{'Could not fetch user data'}</div>;
  }

  const loggedInUser = useLoggedInUser();
  const loggedInUserAaid = loggedInUser?.accountId ?? '';
  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => {
    sendRecognitionModalOpenClickEvent({
      senderAaid: loggedInUserAaid ?? '',
      receiverAaid: profileData.aaid ?? '',
      profileInternalId: pathProfileId ?? '',
    });
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsOpen(false), []);
  const [flags, setFlags] = React.useState<Array<number>>([]);
  const [isReasonSelected, setIsReasonSelected] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | undefined>(
    undefined,
  );
  const sendRecognitionButton = () => {
    sendRecognitionButtonClickEvent({
      recognitionReason: selectedReason ?? '',
      senderAaid: loggedInUserAaid ?? '',
      receiverAaid: profileData.aaid ?? '',
      profileInternalId: pathProfileId ?? '',
    });
    addFlag();
    setIsReasonSelected(false);
    setSelectedReason(undefined);
    //TO DO: After connecting to KMS, the recognition component on profile should refresh to reflect change in recognition
  };

  const addFlag = () => {
    const newFlagId = flags.length + 1;
    const newFlags = flags.slice();
    newFlags.splice(0, 0, newFlagId);

    setFlags(newFlags);
    closeModal();
  };

  const handleDismiss = () => {
    setFlags(flags.slice(1));
  };

  const userName = userData.nickname;
  const firstName = userName ? userName.split(' ')[0] : null;
  return (
    <div>
      <button css={cursorStyles} onClick={openModal}>
        {`Give ${firstName} recognition`}
      </button>

      {isOpen && (
        <Modal onClose={closeModal}>
          <div css={GiveRecognitionModalStyles}>
            <ModalHeader>
              <div>
                <h2 css={giveRecognitionHeaderStyle}>{'Give recognition'}</h2>
                <button
                  type="button"
                  css={closeButtonStyles}
                  onClick={closeModal}
                >
                  <CrossIcon
                    label="Close Modal"
                    size="large"
                    primaryColor={token('color.text')}
                  />
                </button>
              </div>
            </ModalHeader>

            <KudosModalBody
              onReasonSelect={(reason) => {
                setIsReasonSelected(true);
                setSelectedReason(reason);
              }}
            />

            <ModalFooter>
              <div css={giveRecognitionFooterStyles}>
                <p css={recognitionAmountStyles}>{'+100 PTS'}</p>
                <div>
                  <button
                    onClick={sendRecognitionButton}
                    type="submit"
                    css={[
                      giveRecognitionButton,
                      isReasonSelected && giveRecognitionButtonActive,
                    ]}
                    disabled={!isReasonSelected}
                  >
                    {'Give recognition'}
                  </button>
                </div>
              </div>
            </ModalFooter>
          </div>
        </Modal>
      )}

      <FlagGroup onDismissed={handleDismiss}>
        {flags.map((flagId) => {
          return (
            <AutoDismissFlag
              id={flagId}
              icon={
                <SuccessIcon
                  label="Success"
                  primaryColor={token('color.icon.success')}
                  size="medium"
                />
              }
              key={flagId}
              title={`Peer recognition sent`}
              description={`Great job! ${userData.nickname} received 100 bonus points from your recognition.`}
            />
          );
        })}
      </FlagGroup>
    </div>
  );
};

const GiveRecognitionModalStyles = css({
  '&, *': {
    boxSizing: 'border-box',
  },
  width: '608px',
  height: '430px',
  backgroundColor: 'white',
});

const closeButtonStyles = css({
  position: 'absolute',
  right: '16px',
  top: '24px',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  borderRadius: '4px',
});

const giveRecognitionButton = css({
  position: 'absolute',
  right: '16px',
  bottom: '16px',
  display: 'flex',
  height: '40px',
  padding: `${token('space.100')} ${token('space.150')}`,
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  color: token('color.text.disabled'),
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
  backgroundColor: token('color.background.accent.gray.subtler'),
  borderRadius: '4px',
  borderStyle: 'none',
  cursor: 'pointer',
});

const giveRecognitionButtonActive = css({
  backgroundColor: token('color.background.accent.blue.bolder'),
  color: token('color.text.inverse'),
  cursor: 'pointer',
});

const giveRecognitionHeaderStyle = css({
  color: '#000',
  fontFamily: token('font.family.brand.heading'),
  fontWeight: 'var(--font-weight-bold-700, 700)',
  lineHeight: 'var(--Font-Heading-Line-Height-H4-Heading, 32px)',
});

const recognitionAmountStyles = css({
  display: 'flex',
  height: '40px',
  padding: `${token('space.100')} ${token('space.150')}`,
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  position: 'absolute',
  left: '16px',
  bottom: '16px',
  color: 'var(--Extended-Lime-L600, #6A9A23)',
  textAlign: 'center',
  fontFamily: token('font.family.brand.body'),
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '20px' /* 125% */,
});

const giveRecognitionFooterStyles = css({
  display: 'flex',
  padding: '24px 0px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
});

const cursorStyles = css({
  cursor: 'pointer',
  ':hover': {
    textDecoration: 'underline',
  },
  color: token('color.text.brand'),
  textAlign: 'center',
  fontFamily: token('font.family.brand.body'),
  fontSize: '16px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  border: 'none',
  backgroundColor: 'white',
  padding: '0px',
});

export default KudosModal;
